import React, {forwardRef, useCallback, useRef} from 'react';
import type {ChangeEvent, DragEvent, MouseEvent} from 'react';

import {
    CircleExclamation as DefaultErrorIcon,
    CloudArrowUpIn as DefaultIcon,
} from '@gravity-ui/icons';
import {Button, Text, useActionHandlers, useForkRef} from '@gravity-ui/uikit';

import {block} from '../cn';
import {useDragHover} from '../useDragHover/useDragHover';

import i18n from './i18n';

import './FileDropZone.scss';

const b = block('file-drop-zone');

const onControlsContainerClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
};

export interface FileDropZoneProps {
    view?: 'full' | 'compact';
    onAdd: (files: File[]) => void;
    className?: string;
    title?: string;
    description?: string;
    buttonText?: string;
    multiple?: boolean;
    accept?: string;
    disabled?: boolean;
    error?: string;
    additionalControls?: React.ReactNode;
}

export const FileDropZone = forwardRef<HTMLInputElement, FileDropZoneProps>(
    (
        {
            view = 'full',
            onAdd,
            className,
            title,
            description,
            buttonText,
            disabled,
            accept,
            multiple,
            error,
            additionalControls,
        },
        fileInputRef,
    ) => {
        const fieldRef = useRef<HTMLDivElement>(null);

        const inputRef = useRef<HTMLInputElement>(null);
        const innerFileInputRef = useForkRef(fileInputRef, inputRef);

        const isFieldDragHover = useDragHover(fieldRef);

        const handleInput = useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
                if (e.defaultPrevented) return;

                const files = Array.from(e.target.files || []);

                e.target.value = '';

                onAdd(files);
            },
            [onAdd],
        );

        const handleDrop = useCallback(
            (e: DragEvent<HTMLDivElement>) => {
                e.preventDefault();

                const files = Array.from(e.dataTransfer.files);
                onAdd(files);
            },
            [onAdd],
        );

        const handleClick = useCallback(() => {
            inputRef.current?.click();
        }, []);
        const {onKeyDown} = useActionHandlers(handleClick);

        const isError = Boolean(error);
        const postfix = multiple ? 'multiple' : 'single';

        const defaultTitle = title ?? i18n(`label_title-${view}-${postfix}`);

        const displayTitle = isError ? error : defaultTitle;
        const DisplayIcon = isError ? DefaultErrorIcon : DefaultIcon;

        const displayButtonText = buttonText ?? i18n(`button_select-file-${postfix}`);

        return (
            <div
                ref={fieldRef}
                role="button"
                className={b(
                    {
                        view,
                        error: isError,
                        disabled,
                        'drag-hover': isFieldDragHover,
                    },
                    className,
                )}
                tabIndex={0}
                onClick={handleClick}
                onDrop={handleDrop}
                onKeyDown={onKeyDown}
            >
                {isFieldDragHover ? (
                    <Text>{i18n('label_drop-zone-hovered')}</Text>
                ) : (
                    <>
                        <DisplayIcon className={b('icon', {error: isError})} />
                        <Text variant="subheader-1">{displayTitle}</Text>
                        {view === 'full' && (
                            <>
                                <Text variant="body-1" color="secondary">
                                    {description}
                                </Text>
                                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                                <div
                                    onClick={onControlsContainerClick}
                                    className={b('controls-container')}
                                >
                                    <Button onClick={handleClick} disabled={disabled}>
                                        {displayButtonText}
                                    </Button>
                                    {additionalControls}
                                </div>
                            </>
                        )}
                    </>
                )}
                <input
                    ref={innerFileInputRef}
                    type="file"
                    className={b('input')}
                    onInput={handleInput}
                    accept={accept}
                    multiple={multiple}
                    autoComplete="off"
                    disabled={disabled}
                    tabIndex={-1}
                    hidden
                />
            </div>
        );
    },
);

FileDropZone.displayName = 'FileDropZone';
